'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { PokerSiteLogo } from '../Dashboard/PokerSiteLogo';
import { format, parseISO } from 'date-fns';
import { fr, enUS, es, it, de, pt, type Locale } from 'date-fns/locale';
import { useStepperContext } from '../ui/stepper';
import { useSessionFooter } from './NewSessionStepper';
import { useDefaultCurrency } from '@/hooks/useDefaultCurrency';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { minutesToHoursMinutes, hoursMinutesToMinutes, durationInputValue } from '@/lib/duration';
import { Input } from '../ui/input';

interface SessionData {
  format?: string;
  universe?: 'Online' | 'Live';
  date?: Date;
  duration?: string;
  tournamentCount?: string;
  buyInTotal?: string;
  stakes?: string;
  customStakes?: string;
  tableType?: string;
  variant?: string;
  customVariant?: string;
  handsCount?: string;
  cashGameVariantLines?: { bankrollId?: string; variant: string; stakes: string; tableType?: string | null; handsCount?: string | number | null; profit?: string | number | null }[] | null;
  buyInLevel?: string;
  bankrolls?: Map<string, {
    bankrollId: string;
    newBalance: string;
    rackback?: string;
    tournamentCount?: string;
    buyInTotal?: string;
    spinGoDetails?: { gameType: string; durationMinutes?: string; buyInLevel?: string; tournamentCount: string; buyInTotal: string; rackback: string; profit?: string }[];
  }>;
  notes?: string;
  sessionProfit?: string;
  sessionProfitCurrency?: string;
  tournamentName?: string;
  mttLiveResultType?: 'ITM' | 'Eliminated';
  mttLiveEntryCount?: string;
  userFestivalId?: string | null;
  pokerFestivalId?: string | null;
  pokerTournamentId?: string | null;
  userTournamentId?: string | null;
  cashLiveRoomId?: string;
  cashLiveRoomName?: string;
  cashLiveDurationMinutes?: number | null;
  cashLiveCurrency?: string;
  cashLiveLimits?: string | null;
  cashLiveVariant?: string | null;
  cashLiveBuyIn?: number;
  cashLiveCashout?: number;
  cashLiveNotes?: string | null;
}

interface Bankroll {
  id: string;
  siteName: string;
  currentBalance: string;
  currency: string;
}

interface NewSessionStep3SummaryProps {
  sessionData: SessionData;
  editSessionId?: string | null;
  returnTo?: string | null;
  onComplete: () => void;
}

export const NewSessionStep3Summary: React.FC<NewSessionStep3SummaryProps> = ({
  sessionData,
  editSessionId,
  returnTo,
  onComplete,
}) => {
  const t = useTranslations('sessions.new.stepper.step3');
  const tManual = useTranslations('sessions.new.manual');
  const locale = useLocale();
  const router = useRouter();
  const { previousStep } = useStepperContext();
  const setFooter = useSessionFooter()?.setFooter;
  const { defaultCurrency } = useDefaultCurrency();
  
  const [bankrolls, setBankrolls] = useState<Bankroll[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [totalResult, setTotalResult] = useState<number>(0);
  const [isCalculatingTotal, setIsCalculatingTotal] = useState(false);
  const [totalRackback, setTotalRackback] = useState<number>(0);
  const [notes, setNotes] = useState(sessionData.notes ?? '');
  const isMTT = sessionData.format === 'MTT';
  const durationFromSession = minutesToHoursMinutes(sessionData.duration ? parseInt(sessionData.duration, 10) : null);
  const [durationHoursRecap, setDurationHoursRecap] = useState(durationInputValue(durationFromSession.hours));
  const [durationMinutesRecap, setDurationMinutesRecap] = useState(durationInputValue(durationFromSession.minutes));
  const [tournamentCountRecap, setTournamentCountRecap] = useState(sessionData.tournamentCount ?? '');
  const [buyInTotalRecap, setBuyInTotalRecap] = useState(sessionData.buyInTotal ?? '');
  const [incompleteConfirmOpen, setIncompleteConfirmOpen] = useState(false);

  useEffect(() => {
    setNotes(sessionData.notes ?? '');
  }, [sessionData.notes]);

  useEffect(() => {
    const { hours, minutes } = minutesToHoursMinutes(sessionData.duration ? parseInt(sessionData.duration, 10) : null);
    setDurationHoursRecap(durationInputValue(hours));
    setDurationMinutesRecap(durationInputValue(minutes));
    setTournamentCountRecap(sessionData.tournamentCount ?? '');
    setBuyInTotalRecap(sessionData.buyInTotal ?? '');
  }, [sessionData.duration, sessionData.tournamentCount, sessionData.buyInTotal]);

  useEffect(() => {
    const fetchBankrolls = async () => {
      try {
        const response = await fetch('/api/bankrolls');
        const data = await response.json();
        if (data.bankrolls) {
          setBankrolls(data.bankrolls);
        }
      } catch (err) {
        console.error('Error fetching bankrolls:', err);
      }
    };

    fetchBankrolls();
  }, []);

  const dateLocaleMap: Record<string, Locale> = { fr, en: enUS, es, it, de, pt };
  const intlLocaleMap: Record<string, string> = {
    fr: 'fr-FR',
    en: 'en-US',
    es: 'es-ES',
    it: 'it-IT',
    de: 'de-DE',
    pt: 'pt-PT',
  };
  const dateLocale = dateLocaleMap[locale] || fr;
  const intlLocale = intlLocaleMap[locale] || 'fr-FR';

  const formatCurrency = (amount: number, currency: string): string => {
    return new Intl.NumberFormat(intlLocale, {
      style: 'currency',
      currency: currency,
      currencyDisplay: 'symbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (date?: Date) => {
    if (!date) return '-';
    return format(date, 'EEEE d MMMM yyyy', { locale: dateLocale });
  };

  const formatStakes = (stakes?: string, customStakes?: string): string => {
    if (customStakes) return customStakes;
    if (!stakes) return '-';
    
    try {
      const parsed = JSON.parse(stakes);
      if (Array.isArray(parsed)) {
        return parsed.join(', ');
      }
      return stakes;
    } catch {
      return stakes;
    }
  };

  const formatVariant = (variant?: string, customVariant?: string): string => {
    if (customVariant) return customVariant;
    if (!variant) return '-';
    
    try {
      const parsed = JSON.parse(variant);
      if (Array.isArray(parsed)) {
        return parsed.join(', ');
      }
      return variant;
    } catch {
      return variant;
    }
  };

  const calculateBankrollResult = useCallback((bankrollId: string): number => {
    const selected = sessionData.bankrolls?.get(bankrollId);
    if (!selected) return 0;
    
    const bankroll = bankrolls.find(b => b.id === bankrollId);
    if (!bankroll) return 0;
    
    const isSpinGo = sessionData.format === 'Spin & Go' || sessionData.format === 'Spin&Go';
    const hasSpinGoDetailsWithProfit = isSpinGo && selected.spinGoDetails?.length && selected.spinGoDetails.some((d) => d.profit != null && d.profit !== '');
    if (hasSpinGoDetailsWithProfit && selected.spinGoDetails) {
      const sumProfit = selected.spinGoDetails.reduce(
        (s, d) => s + (d.profit != null && d.profit !== '' ? parseFloat(d.profit) : 0),
        0
      );
      return sumProfit;
    }
    
    const currentBalance = parseFloat(bankroll.currentBalance);
    const newBalance = parseFloat(selected.newBalance);
    return newBalance - currentBalance;
  }, [sessionData.bankrolls, sessionData.format, bankrolls]);

  const calculateTotalResult = async (): Promise<number> => {
    if (!sessionData.bankrolls || !defaultCurrency) return 0;
    
    setIsCalculatingTotal(true);
    let total = 0;
    
    try {
      const conversionPromises = Array.from(sessionData.bankrolls.keys()).map(async (bankrollId) => {
        const result = calculateBankrollResult(bankrollId);
        const bankroll = bankrolls.find(b => b.id === bankrollId);
        
        if (!bankroll || result === 0) return 0;
        
        // Si la devise est la même que la devise par défaut, pas besoin de conversion
        if (bankroll.currency === defaultCurrency) {
          return result;
        }
        
        // Convertir dans la devise par défaut
        try {
          const response = await fetch(
            `/api/currency/convert?amount=${result}&from=${bankroll.currency}&to=${defaultCurrency}`
          );
          if (response.ok) {
            const data = await response.json();
            return data.convertedAmount || 0;
          }
        } catch (err) {
          console.error(`Error converting ${bankroll.currency} to ${defaultCurrency}:`, err);
        }
        
        return 0;
      });
      
      const convertedResults = await Promise.all(conversionPromises);
      total = convertedResults.reduce((sum, amount) => sum + amount, 0);
    } finally {
      setIsCalculatingTotal(false);
    }
    
    return total;
  };

  const hasIncompleteFields = useCallback(() => {
    const isCashLiveRoom = sessionData.format === 'Cash Game' && sessionData.universe === 'Live';
    if (isCashLiveRoom) return false;
    const isMTTLiveNoBankroll = sessionData.format === 'MTT' && sessionData.universe === 'Live' && !sessionData.bankrolls?.size;
    if (isMTTLiveNoBankroll) return false;
    const totalDurationMinutes = hoursMinutesToMinutes(parseInt(durationHoursRecap, 10) || 0, parseInt(durationMinutesRecap, 10) || 0);
    if (totalDurationMinutes === 0) return true;
    const isMTTOnline = sessionData.format === 'MTT' && sessionData.universe === 'Online';
    const isCashGame = sessionData.format === 'Cash Game';
    if (isCashGame) return false;
    const bankrollsMap = sessionData.bankrolls;
    if (!bankrollsMap?.size) return false;
    for (const [, sel] of bankrollsMap) {
      const newBalanceEmpty = sel.newBalance == null || String(sel.newBalance).trim() === '';
      if (newBalanceEmpty) return true;
      if (isMTTOnline) {
        const tournamentsEmpty = sel.tournamentCount == null || String(sel.tournamentCount).trim() === '';
        const buyInEmpty = sel.buyInTotal == null || String(sel.buyInTotal).trim() === '';
        if (tournamentsEmpty || buyInEmpty) return true;
      }
    }
    return false;
  }, [sessionData.format, sessionData.universe, sessionData.bankrolls, durationHoursRecap, durationMinutesRecap]);

  const getIncompleteFieldLabels = useCallback((): string[] => {
    const labels: string[] = [];
    const totalDurationMinutes = hoursMinutesToMinutes(parseInt(durationHoursRecap, 10) || 0, parseInt(durationMinutesRecap, 10) || 0);
    if (totalDurationMinutes === 0) labels.push(tManual('sessionInfo.duration'));
    const isMTTOnline = sessionData.format === 'MTT' && sessionData.universe === 'Online';
    const isCashGame = sessionData.format === 'Cash Game';
    if (isCashGame) return labels;
    const bankrollsMap = sessionData.bankrolls;
    if (!bankrollsMap?.size) return labels;
    for (const [bankrollId, sel] of bankrollsMap) {
      const siteName = bankrolls.find(b => b.id === bankrollId)?.siteName ?? '';
      const suffix = siteName ? ` (${siteName})` : '';
      const newBalanceEmpty = sel.newBalance == null || String(sel.newBalance).trim() === '';
      if (newBalanceEmpty) labels.push(`${tManual('bankrolls.tableNewBalance')}${suffix}`);
      if (isMTTOnline) {
        const tournamentsEmpty = sel.tournamentCount == null || String(sel.tournamentCount).trim() === '';
        const buyInEmpty = sel.buyInTotal == null || String(sel.buyInTotal).trim() === '';
        if (tournamentsEmpty) labels.push(`${tManual('bankrolls.tableTournaments')}${suffix}`);
        if (buyInEmpty) labels.push(`${tManual('bankrolls.tableBuyInTotal')}${suffix}`);
      }
    }
    return labels;
  }, [sessionData.format, sessionData.universe, sessionData.bankrolls, durationHoursRecap, durationMinutesRecap, bankrolls, tManual]);

  const handleSubmit = useCallback(async () => {
    setError('');
    setIsSubmitting(true);

    try {
      const isCashLiveRoom = sessionData.format === 'Cash Game' && sessionData.universe === 'Live';
      if (isCashLiveRoom && sessionData.cashLiveBuyIn != null && sessionData.cashLiveCashout != null) {
        const res = await fetch('/api/cash-live/sessions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...(sessionData.cashLiveRoomId ? { roomId: sessionData.cashLiveRoomId } : {}),
            date: sessionData.date?.toISOString(),
            durationMinutes: sessionData.cashLiveDurationMinutes ?? null,
            currency: sessionData.cashLiveCurrency || 'EUR',
            limits: sessionData.cashLiveLimits ?? null,
            variant: sessionData.cashLiveVariant ?? null,
            buyIn: sessionData.cashLiveBuyIn,
            cashout: sessionData.cashLiveCashout,
            notes: sessionData.cashLiveNotes?.trim() || null,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Erreur lors de l\'ajout de la session');
        onComplete();
        return;
      }

      const url = editSessionId ? `/api/sessions/${editSessionId}` : '/api/sessions';
      const method = editSessionId ? 'PUT' : 'POST';
      const isMTTOnline = sessionData.format === 'MTT' && sessionData.universe === 'Online';
      const isSpinGo = sessionData.format === 'Spin & Go' || sessionData.format === 'Spin&Go';
      const perBankrollTournamentBuyIn = isMTTOnline || isSpinGo;

      const bankrollUpdates = Array.from(sessionData.bankrolls?.values() || []).map(sb => {
        const hasSpinGoDetails = isSpinGo && sb.spinGoDetails?.length && sb.spinGoDetails.some(
          (d) => (d.tournamentCount && parseInt(d.tournamentCount, 10) > 0) || (d.profit != null && d.profit !== '')
        );
        const bankroll = bankrolls.find(b => b.id === sb.bankrollId);
        const currentBalance = bankroll ? parseFloat(bankroll.currentBalance) : 0;
        const spinGoNewBalance = isSpinGo && hasSpinGoDetails && sb.spinGoDetails
          ? currentBalance + sb.spinGoDetails.reduce((s, d) => s + (d.profit != null && d.profit !== '' ? parseFloat(d.profit) : 0), 0)
          : null;
        const payload: {
          bankrollId: string;
          newBalance: number;
          profit?: number | null;
          rackback?: number | null;
          tournamentCount?: number;
          buyInTotal?: number;
          spinGoDetails?: { gameType: string; buyInLevel?: number | null; durationMinutes?: number | null; tournamentCount: number; buyInTotal: number; rackback?: number | null; profit?: number | null; cev?: number | null }[];
        } = {
          bankrollId: sb.bankrollId,
          newBalance: spinGoNewBalance != null ? spinGoNewBalance : parseFloat(sb.newBalance),
          profit: spinGoNewBalance != null
            ? spinGoNewBalance - currentBalance
            : parseFloat(sb.newBalance) - currentBalance,
          ...(hasSpinGoDetails ? {} : { rackback: sb.rackback ? parseFloat(sb.rackback) : null }),
        };
        if (perBankrollTournamentBuyIn) {
          if (hasSpinGoDetails && sb.spinGoDetails) {
            payload.spinGoDetails = sb.spinGoDetails
              .filter((d) => (d.tournamentCount && parseInt(d.tournamentCount, 10) > 0) || (d.profit != null && d.profit !== ''))
              .map((d) => {
                const buyInLevel = d.buyInLevel != null && d.buyInLevel !== '' ? parseInt(d.buyInLevel, 10) : 0;
                const tournamentCount = parseInt(d.tournamentCount, 10) || 0;
                const buyInTotal = buyInLevel * tournamentCount;
                return {
                  gameType: d.gameType,
                  buyInLevel: d.buyInLevel != null && d.buyInLevel !== '' ? buyInLevel : null,
                  durationMinutes: d.durationMinutes != null && d.durationMinutes !== '' ? parseInt(d.durationMinutes, 10) : null,
                  tournamentCount,
                  buyInTotal,
                  rackback: d.rackback && parseFloat(d.rackback) > 0 ? parseFloat(d.rackback) : null,
                  profit: d.profit != null && d.profit !== '' ? parseFloat(d.profit) : null,
                  cev: (d as { cev?: string }).cev != null && (d as { cev?: string }).cev !== '' ? parseFloat((d as { cev?: string }).cev!) : null,
                };
              });
          } else {
            payload.tournamentCount = sb.tournamentCount !== undefined && sb.tournamentCount !== '' ? parseInt(sb.tournamentCount, 10) : 0;
            payload.buyInTotal = sb.buyInTotal !== undefined && sb.buyInTotal !== '' ? parseFloat(sb.buyInTotal) : 0;
          }
        }
        return payload;
      });

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          format: sessionData.format,
          universe: sessionData.universe,
          date: sessionData.date?.toISOString(),
          duration: (() => {
            const total = hoursMinutesToMinutes(parseInt(durationHoursRecap, 10) || 0, parseInt(durationMinutesRecap, 10) || 0);
            return total > 0 ? total : null;
          })(),
          tournamentCount: perBankrollTournamentBuyIn ? undefined : (isMTT && sessionData.universe === 'Live' ? 1 : (sessionData.tournamentCount ? parseInt(sessionData.tournamentCount) : null)),
          buyInTotal: perBankrollTournamentBuyIn ? undefined : (isMTT && sessionData.universe === 'Live' ? (buyInTotalRecap ? parseFloat(buyInTotalRecap) : null) : (sessionData.buyInTotal ? parseFloat(sessionData.buyInTotal) : null)),
          itmCount: isMTT && sessionData.universe === 'Live' && !sessionData.bankrolls?.size ? (sessionData.mttLiveResultType === 'ITM' ? 1 : 0) : undefined,
          tournamentName: isMTT && sessionData.universe === 'Live' && sessionData.tournamentName ? sessionData.tournamentName.trim() || null : undefined,
          stakes: sessionData.format === 'Cash Game' && !sessionData.cashGameVariantLines?.length && (sessionData.customStakes || sessionData.stakes) ? (sessionData.customStakes || sessionData.stakes) : null,
          tableType: sessionData.format === 'Cash Game' && !sessionData.cashGameVariantLines?.length && sessionData.tableType ? sessionData.tableType : null,
          variant: sessionData.format === 'Cash Game' && !sessionData.cashGameVariantLines?.length && (sessionData.customVariant || sessionData.variant) ? (sessionData.customVariant || sessionData.variant) : null,
          handsCount: sessionData.format === 'Cash Game' && !sessionData.cashGameVariantLines?.length && sessionData.handsCount ? parseInt(sessionData.handsCount) : null,
          cashGameVariantLines: sessionData.format === 'Cash Game' && sessionData.cashGameVariantLines?.length
            ? sessionData.cashGameVariantLines.map(l => ({
                bankrollId: (l as { bankrollId?: string }).bankrollId ?? '',
                variant: l.variant,
                stakes: l.stakes,
                tableType: l.tableType ?? null,
                handsCount: l.handsCount != null ? l.handsCount : null,
                profit: (l as { profit?: number | null }).profit != null ? (l as { profit?: number | null }).profit : null,
              }))
            : null,
          buyInLevel: (sessionData.format === 'Spin & Go' || sessionData.format === 'Spin&Go') && sessionData.buyInLevel ? parseInt(sessionData.buyInLevel, 10) : null,
          bankrollUpdates,
          notes: notes.trim() || null,
          sessionProfit: (() => {
            if (!(isMTT && sessionData.universe === 'Live') || sessionData.sessionProfit == null || sessionData.sessionProfit === '') return undefined;
            const gross = parseFloat(sessionData.sessionProfit);
            if (isNaN(gross)) return undefined;
            // MTT Live sans bankroll ITM : sessionProfit = gains bruts ; on envoie le net (gains bruts - total buy-in)
            if (!sessionData.bankrolls?.size && sessionData.mttLiveResultType === 'ITM') {
              const buyIn = buyInTotalRecap ? parseFloat(buyInTotalRecap) : (sessionData.buyInTotal ? parseFloat(sessionData.buyInTotal) : 0) || 0;
              const reEntry = parseInt(sessionData.mttLiveEntryCount || '0', 10) || 0;
              const totalBuyIn = buyIn * (1 + reEntry);
              return gross - totalBuyIn;
            }
            return gross;
          })(),
          sessionProfitCurrency: isMTT && sessionData.universe === 'Live' && sessionData.sessionProfitCurrency ? sessionData.sessionProfitCurrency : undefined,
          userFestivalId: isMTT && sessionData.universe === 'Live' && sessionData.userFestivalId ? sessionData.userFestivalId.trim() || null : undefined,
          pokerFestivalId: isMTT && sessionData.universe === 'Live' && sessionData.pokerFestivalId ? sessionData.pokerFestivalId.trim() || null : undefined,
          pokerTournamentId: isMTT && sessionData.universe === 'Live' && sessionData.pokerTournamentId ? sessionData.pokerTournamentId.trim() || null : undefined,
          userTournamentId: isMTT && sessionData.universe === 'Live' && sessionData.userTournamentId ? sessionData.userTournamentId.trim() || null : undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || (editSessionId ? 'Erreur lors de la mise à jour de la session' : 'Erreur lors de la création de la session'));
      }

      // Fermer la modal en appelant onComplete (qui gère la redirection si nécessaire)
      onComplete();
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setIsSubmitting(false);
    }
  }, [sessionData, bankrolls, editSessionId, notes, durationHoursRecap, durationMinutesRecap, tournamentCountRecap, buyInTotalRecap, isMTT, onComplete]);

  const handleValidateClick = useCallback(() => {
    if (hasIncompleteFields()) {
      setIncompleteConfirmOpen(true);
      return;
    }
    handleSubmit();
  }, [hasIncompleteFields, handleSubmit]);

  const handleIncompleteConfirmContinue = useCallback(() => {
    setIncompleteConfirmOpen(false);
    handleSubmit();
  }, [handleSubmit]);

  const handleValidateClickRef = useRef(handleValidateClick);
  handleValidateClickRef.current = handleValidateClick;

  useEffect(() => {
    if (!setFooter) return;
    setFooter({
      secondary: {
        label: t('previous'),
        onClick: previousStep,
      },
      primary: {
        label: isSubmitting ? 'Validation...' : t('validate'),
        disabled: isSubmitting,
        onClick: () => handleValidateClickRef.current?.(),
      },
    });
    return () => setFooter(null);
  }, [setFooter, t, isSubmitting, previousStep]);

  const isCashLiveRoom = sessionData.format === 'Cash Game' && sessionData.universe === 'Live';

  // Calculer le total avec conversion de devises et le rackback (toujours appelé pour respecter les règles des hooks)
  useEffect(() => {
    if (isCashLiveRoom) {
      setTotalResult(0);
      setTotalRackback(0);
      return;
    }
    if (defaultCurrency && sessionData.bankrolls && sessionData.bankrolls.size > 0 && bankrolls.length > 0) {
      const calculateTotal = async () => {
        if (!sessionData.bankrolls || !defaultCurrency) {
          setTotalResult(0);
          setTotalRackback(0);
          return;
        }
        
        setIsCalculatingTotal(true);
        let total = 0;
        let rackbackTotal = 0;
        
        try {
          const conversionPromises = Array.from(sessionData.bankrolls.keys()).map(async (bankrollId) => {
            const result = calculateBankrollResult(bankrollId);
            const selected = sessionData.bankrolls?.get(bankrollId);
            const bankroll = bankrolls.find(b => b.id === bankrollId);
            
            if (!bankroll) return { result: 0, rackback: 0 };
            
            // Calculer le résultat converti
            let convertedResult = 0;
            if (result !== 0) {
              // Si la devise est la même que la devise par défaut, pas besoin de conversion
              if (bankroll.currency === defaultCurrency) {
                convertedResult = result;
              } else {
                // Convertir dans la devise par défaut
                try {
                  const response = await fetch(
                    `/api/currency/convert?amount=${result}&from=${bankroll.currency}&to=${defaultCurrency}`
                  );
                  if (response.ok) {
                    const data = await response.json();
                    convertedResult = data.convertedAmount || 0;
                  }
                } catch (err) {
                  console.error(`Error converting ${bankroll.currency} to ${defaultCurrency}:`, err);
                }
              }
            }
            
            // Calculer le rackback converti (spinGoDetails ou champ unique)
            let convertedRackback = 0;
            const rackbackFromDetails = selected?.spinGoDetails?.reduce(
              (s, d) => s + (d.rackback && parseFloat(d.rackback) > 0 ? parseFloat(d.rackback) : 0),
              0
            ) ?? 0;
            const rackbackToConvert = rackbackFromDetails > 0 ? rackbackFromDetails : (selected?.rackback && parseFloat(selected.rackback) > 0 ? parseFloat(selected.rackback) : 0);
            if (rackbackToConvert > 0) {
              if (bankroll.currency === defaultCurrency) {
                convertedRackback = rackbackToConvert;
              } else {
                try {
                  const response = await fetch(
                    `/api/currency/convert?amount=${rackbackToConvert}&from=${bankroll.currency}&to=${defaultCurrency}`
                  );
                  if (response.ok) {
                    const data = await response.json();
                    convertedRackback = data.convertedAmount || 0;
                  }
                } catch (err) {
                  console.error(`Error converting rackback ${bankroll.currency} to ${defaultCurrency}:`, err);
                }
              }
            }

            return { result: convertedResult, rackback: convertedRackback };
          });
          
          const convertedResults = await Promise.all(conversionPromises);
          total = convertedResults.reduce((sum, item) => sum + item.result, 0);
          rackbackTotal = convertedResults.reduce((sum, item) => sum + item.rackback, 0);
        } finally {
          setIsCalculatingTotal(false);
        }
        
        setTotalResult(total);
        setTotalRackback(rackbackTotal);
      };
      
      calculateTotal();
    } else if (defaultCurrency && sessionData.format === 'MTT' && sessionData.universe === 'Live' && !sessionData.bankrolls?.size && sessionData.sessionProfit != null && sessionData.sessionProfit !== '') {
      // Tournoi isolé MTT Live : résultat total = gain net (gains bruts - total buy-in)
      (async () => {
        const gross = parseFloat(sessionData.sessionProfit!);
        const buyIn = buyInTotalRecap ? parseFloat(buyInTotalRecap) : (sessionData.buyInTotal ? parseFloat(sessionData.buyInTotal) : 0) || 0;
        const reEntry = parseInt(sessionData.mttLiveEntryCount || '0', 10) || 0;
        let net = gross - buyIn * (1 + reEntry);
        const sessionCur = sessionData.sessionProfitCurrency || 'EUR';
        if (sessionCur !== defaultCurrency) {
          try {
            const response = await fetch(
              `/api/currency/convert?amount=${Math.abs(net)}&from=${sessionCur}&to=${defaultCurrency}`
            );
            if (response.ok) {
              const data = await response.json();
              net = net < 0 ? -(data.convertedAmount || 0) : (data.convertedAmount || 0);
            }
          } catch (err) {
            console.error(`Error converting session profit ${sessionCur} to ${defaultCurrency}:`, err);
          }
        }
        setTotalResult(net);
        setTotalRackback(0);
      })();
    } else {
      setTotalResult(0);
      setTotalRackback(0);
    }
  }, [isCashLiveRoom, defaultCurrency, sessionData.bankrolls, sessionData.format, sessionData.universe, sessionData.sessionProfit, sessionData.sessionProfitCurrency, sessionData.buyInTotal, sessionData.mttLiveEntryCount, buyInTotalRecap, bankrolls, calculateBankrollResult]);

  const selectedBankrollsList = Array.from(sessionData.bankrolls?.keys() || []);
  const bankrollNames = selectedBankrollsList
    .map((id) => bankrolls.find((b) => b.id === id)?.siteName)
    .filter(Boolean) as string[];

  const cashLiveResult = isCashLiveRoom && sessionData.cashLiveBuyIn != null && sessionData.cashLiveCashout != null
    ? sessionData.cashLiveCashout - sessionData.cashLiveBuyIn
    : 0;

  // Récap simplifié pour Session poker room
  if (isCashLiveRoom) {
    return (
      <div className="space-y-6">
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('generalInfo')}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{tManual('sessionInfo.date')}</p>
              <p className="font-medium text-gray-900 dark:text-white">{formatDate(sessionData.date)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('pokerRoomLabel')}</p>
              <p className="font-medium text-gray-900 dark:text-white">{sessionData.cashLiveRoomName || t('unassignedSessionsLabel')}</p>
            </div>
            {sessionData.cashLiveDurationMinutes != null && sessionData.cashLiveDurationMinutes > 0 && (
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{tManual('sessionInfo.duration')}</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {Math.floor(sessionData.cashLiveDurationMinutes / 60)}h {sessionData.cashLiveDurationMinutes % 60}min
                </p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('limitsVariantLabel')}</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {sessionData.cashLiveLimits || '-'} — {sessionData.cashLiveVariant || '-'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('buyInCashoutLabel')}</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {formatCurrency(sessionData.cashLiveBuyIn ?? 0, sessionData.cashLiveCurrency || 'EUR')} / {formatCurrency(sessionData.cashLiveCashout ?? 0, sessionData.cashLiveCurrency || 'EUR')}
              </p>
            </div>
          </div>
        </div>
        <div className={cashLiveResult >= 0 ? 'bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 p-6' : 'bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 p-6'}>
          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('totalResult')}</h4>
          <p className={`text-2xl font-bold ${cashLiveResult >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {cashLiveResult >= 0 ? '+' : ''}{formatCurrency(cashLiveResult, sessionData.cashLiveCurrency || 'EUR')}
          </p>
        </div>
        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* General Information */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('generalInfo')}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{tManual('sessionInfo.date')}</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {formatDate(sessionData.date)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">{tManual('sessionInfo.duration')}</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {isMTT
                  ? (() => {
                      const h = parseInt(durationHoursRecap, 10) || 0;
                      const m = parseInt(durationMinutesRecap, 10) || 0;
                      if (h > 0 && m > 0) return `${h}h ${m}min`;
                      if (h > 0) return `${h}h`;
                      return m > 0 ? `${m}min` : '-';
                    })()
                  : sessionData.duration
                    ? (() => {
                        const { hours, minutes } = minutesToHoursMinutes(parseInt(sessionData.duration!, 10));
                        if (hours > 0 && minutes > 0) return `${hours}h ${minutes}min`;
                        if (hours > 0) return `${hours}h`;
                        return `${minutes}min`;
                      })()
                    : '-'}
              </p>
            </div>
          </div>
          {(sessionData.format === 'MTT' || sessionData.format === 'Spin & Go' || sessionData.format === 'Spin&Go') && (
            <>
              <div className="flex items-center gap-3">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {sessionData.format === 'Spin & Go' || sessionData.format === 'Spin&Go'
                      ? t('spinGoCountLabel')
                      : sessionData.format === 'MTT' && sessionData.universe === 'Live'
                        ? t('tournamentNameLabel')
                        : t('tournamentCountLabel')}
                  </p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {(sessionData.format === 'MTT' && sessionData.universe === 'Online') || sessionData.format === 'Spin & Go' || sessionData.format === 'Spin&Go'
                      ? (sessionData.bankrolls
                          ? (() => {
                              const hasSpinGoDetails = sessionData.format === 'Spin & Go' || sessionData.format === 'Spin&Go';
                              if (hasSpinGoDetails && Array.from(sessionData.bankrolls.values()).some((sb) => sb.spinGoDetails?.length)) {
                                return Array.from(sessionData.bankrolls.values()).reduce((sum, sb) => {
                                  if (!sb.spinGoDetails) return sum + (sb.tournamentCount ? parseInt(sb.tournamentCount, 10) : 0);
                                  return sum + sb.spinGoDetails.reduce((s, d) => s + (d.tournamentCount ? parseInt(d.tournamentCount, 10) : 0), 0);
                                }, 0) || '-';
                              }
                              return Array.from(sessionData.bankrolls.values()).reduce((sum, sb) => sum + (sb.tournamentCount ? parseInt(sb.tournamentCount, 10) : 0), 0) || '-';
                            })()
                          : '-')
                      : sessionData.format === 'MTT' && sessionData.universe === 'Live'
                        ? (sessionData.tournamentName || '-')
                        : (sessionData.tournamentCount || '-')}
                  </p>
                </div>
              </div>
              {sessionData.format === 'MTT' && (
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t('totalBuyInLabel')}</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {sessionData.universe === 'Online'
                        ? (sessionData.bankrolls && Array.from(sessionData.bankrolls.values()).some((sb) => sb.buyInTotal && parseFloat(sb.buyInTotal) > 0)
                          ? t('totalBuyInByBankrollLabel')
                          : '-')
                        : (() => {
                            const buyIn = buyInTotalRecap ? parseFloat(buyInTotalRecap) : (sessionData.buyInTotal ? parseFloat(sessionData.buyInTotal) : 0) || 0;
                            const reEntry = parseInt(sessionData.mttLiveEntryCount || '0', 10) || 0;
                            const total = buyIn * (1 + reEntry);
                            return total > 0 ? `${total} ${sessionData.sessionProfitCurrency || 'EUR'}` : (sessionData.buyInTotal ? `${sessionData.buyInTotal} €` : '-');
                          })()}
                    </p>
                  </div>
                </div>
              )}
              {sessionData.format === 'MTT' && sessionData.universe === 'Live' && (
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t('reEntriesLabel')}</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {sessionData.mttLiveEntryCount ?? '0'}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
          {sessionData.format === 'Cash Game' && (
            sessionData.cashGameVariantLines?.length ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('variantsLimitsHandsLabel')}</p>
                {sessionData.cashGameVariantLines.map((line, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                    <span className="font-medium text-gray-900 dark:text-white">{line.variant}</span>
                    <span className="text-gray-600 dark:text-gray-400">{t('limitLabel')}: {line.stakes || '-'}</span>
                    {line.tableType && <span className="text-gray-600 dark:text-gray-400">{t('typeLabel')}: {line.tableType}</span>}
                    {line.handsCount != null && Number(line.handsCount) > 0 && (
                      <span className="text-gray-600 dark:text-gray-400">{line.handsCount} mains</span>
                    )}
                    {(line as { profit?: number | null }).profit != null && (line as { profit?: number | null }).profit !== 0 && (
                      <span className="text-gray-600 dark:text-gray-400">{t('profitLossLabel')}: {(line as { profit?: number | null }).profit}</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t('limitLabel')}</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {formatStakes(sessionData.stakes, sessionData.customStakes)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{t('variantLabel')}</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {formatVariant(sessionData.variant, sessionData.customVariant)}
                    </p>
                  </div>
                </div>
              </>
            )
          )}
        </div>
      </div>

      {/* Bankrolls — masqué pour tournoi isolé MTT Live */}
      {!(sessionData.format === 'MTT' && sessionData.universe === 'Live' && !sessionData.bankrolls?.size) && (
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('bankrolls')} ({selectedBankrollsList.length})
        </h4>
        <div className="space-y-4">
          {selectedBankrollsList.map((bankrollId) => {
            const bankroll = bankrolls.find(b => b.id === bankrollId);
            const selected = sessionData.bankrolls?.get(bankrollId);
            if (!bankroll || !selected) return null;

            const currentBalance = parseFloat(bankroll.currentBalance);
            const isSpinGoWithDetails = (sessionData.format === 'Spin & Go' || sessionData.format === 'Spin&Go') && selected.spinGoDetails?.length;
            const newBalance = isSpinGoWithDetails && selected.spinGoDetails
              ? currentBalance + selected.spinGoDetails.reduce((s, d) => s + (d.profit != null && d.profit !== '' ? parseFloat(d.profit) : 0), 0)
              : parseFloat(selected.newBalance);
            const result = newBalance - currentBalance;

            return (
              <div key={bankrollId} className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <PokerSiteLogo
                  siteName={bankroll.siteName}
                  size={40}
                />
                <div className="flex-1 min-w-0">
                  <h5 className="font-semibold text-gray-900 dark:text-white">
                    {bankroll.siteName} {bankroll.currency}
                  </h5>
                  {(() => {
                    const isMTTOrSpinGo = (sessionData.format === 'MTT' && sessionData.universe === 'Online') || sessionData.format === 'Spin & Go' || sessionData.format === 'Spin&Go';
                    const filteredDetails = selected.spinGoDetails?.filter((d) => (d.tournamentCount && parseInt(d.tournamentCount, 10) > 0) || (d.profit != null && d.profit !== '')) ?? [];
                    const totalBuyIn = filteredDetails.reduce((sum, d) => sum + (parseInt(d.buyInLevel || '0', 10) || 0) * (parseInt(d.tournamentCount, 10) || 0), 0);
                    return (
                      <div className="flex flex-col gap-2 mt-1">
                        {/* Ligne 1 : Solde actuel, Nouveau solde, Buy-in total */}
                        <div className="flex flex-wrap items-center gap-4">
                          <div>
                            <p className="text-xs text-gray-600 dark:text-gray-400">{t('currentBalanceLabel')}</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {formatCurrency(currentBalance, bankroll.currency)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600 dark:text-gray-400">{t('newBalanceLabel')}</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {formatCurrency(newBalance, bankroll.currency)}
                            </p>
                          </div>
                          {(sessionData.format === 'MTT' && sessionData.universe === 'Online') && selected.spinGoDetails?.length && totalBuyIn > 0 && (
                            <div>
                              <p className="text-xs text-gray-600 dark:text-gray-400">Buy-in total</p>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {formatCurrency(totalBuyIn, bankroll.currency)}
                              </p>
                            </div>
                          )}
                          {(sessionData.format === 'MTT' && sessionData.universe === 'Online') && !selected.spinGoDetails?.length && (selected.tournamentCount !== undefined || selected.buyInTotal !== undefined) && (
                            <>
                              <div>
                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                  {tManual('bankrolls.tableTournaments')}
                                </p>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">{selected.tournamentCount || '0'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600 dark:text-gray-400">Buy-in total</p>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                  {selected.buyInTotal ? `${selected.buyInTotal} ${bankroll.currency}` : '0'}
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                        {/* Ligne 2 : Détails Normal / Nitro (2 lignes par type) */}
                        {isMTTOrSpinGo && filteredDetails.length > 0 && (
                          <div className="flex flex-col gap-1.5">
                            {filteredDetails.map((d, i) => {
                              const calcBuyInTotal = (parseInt(d.buyInLevel || '0', 10) || 0) * (parseInt(d.tournamentCount, 10) || 0);
                              return (
                                <div key={d.gameType + i} className="flex flex-col gap-0.5">
                                  <div className="flex items-baseline gap-2 flex-wrap">
                                    <span className="text-xs font-medium text-amber-700 dark:text-amber-300">{d.gameType}</span>
                                    <span className="text-xs text-gray-600 dark:text-gray-400">
                                      {d.tournamentCount || '0'} spin{(d.tournamentCount ? parseInt(d.tournamentCount, 10) : 0) !== 1 ? 's' : ''}
                                      {calcBuyInTotal > 0 ? ` · ${formatCurrency(calcBuyInTotal, bankroll.currency)}` : ''}
                                    </span>
                                  </div>
                                  {(d.rackback && parseFloat(d.rackback) > 0) || (d.profit != null && d.profit !== '') ? (
                                    <div className="text-xs text-gray-600 dark:text-gray-400">
                                      {d.rackback && parseFloat(d.rackback) > 0 && `Rackback ${formatCurrency(parseFloat(d.rackback), bankroll.currency)}`}
                                      {d.rackback && parseFloat(d.rackback) > 0 && (d.profit != null && d.profit !== '') && ' · '}
                                      {d.profit != null && d.profit !== '' && (
                                        <>{t('profitLossLabel')} {parseFloat(d.profit) >= 0 ? '+' : ''}{formatCurrency(parseFloat(d.profit), bankroll.currency)}</>
                                      )}
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div className="px-4 py-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('resultLabel')}</p>
                  <p className={`text-lg font-bold ${result >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {result >= 0 ? '+' : ''}{formatCurrency(result, bankroll.currency)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Total Result — masqué pour tournoi isolé MTT Live */}
      {!(sessionData.format === 'MTT' && sessionData.universe === 'Live' && !sessionData.bankrolls?.size) && (
      <div className={totalResult >= 0 ? 'bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 p-6' : 'bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 p-6'}>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {t('totalResult')}
        </h4>
        <p className={`text-2xl font-bold ${totalResult >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {isCalculatingTotal ? (
            <span className="text-gray-500">{t('calculatingLabel')}</span>
          ) : (
            <>
              {(sessionData.format === 'Spin & Go' || sessionData.format === 'Spin&Go') && totalRackback > 0 ? (
                <>
                  {totalResult + totalRackback >= 0 ? '+' : ''}{new Intl.NumberFormat('fr-FR', {
                    style: 'currency',
                    currency: defaultCurrency || 'EUR',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(Math.round(totalResult + totalRackback))}
                  <span className="text-base font-normal text-gray-600 dark:text-gray-400 ml-2">
                    {t('includingRackbackLabel', { amount: new Intl.NumberFormat(intlLocale, {
                      style: 'currency',
                      currency: defaultCurrency || 'EUR',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }).format(Math.round(totalRackback)) })}
                  </span>
                </>
              ) : (
                <>
                  {totalResult >= 0 ? '+' : ''}{new Intl.NumberFormat('fr-FR', {
                    style: 'currency',
                    currency: defaultCurrency || 'EUR',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(Math.round(totalResult))}
                </>
              )}
            </>
          )}
        </p>
      </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <Dialog open={incompleteConfirmOpen} onOpenChange={setIncompleteConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white">
              {tManual('sessionInfo.title')}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="text-gray-600 dark:text-gray-400 space-y-2">
                <p>{tManual('incompleteFieldsConfirm')}</p>
                {incompleteConfirmOpen && (() => {
                  const missing = getIncompleteFieldLabels();
                  if (missing.length === 0) return null;
                  return (
                    <ul className="list-disc list-inside text-sm mt-2 space-y-0.5">
                      {missing.map((label, i) => (
                        <li key={i}>{label}</li>
                      ))}
                    </ul>
                  );
                })()}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIncompleteConfirmOpen(false)}
            >
              {tManual('incompleteFieldsCancel')}
            </Button>
            <Button
              type="button"
              onClick={handleIncompleteConfirmContinue}
            >
              {tManual('incompleteFieldsContinueAnyway')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
